// Pure helpers shared by link-redirect and log-not-found (and unit tests).
// No Deno or network imports so vitest can load this file directly.

const BOT_RE = new RegExp(
  [
    "bot", "crawl", "spider", "slurp", "facebookexternalhit", "facebot", "embedly", "preview",
    "whatsapp", "telegram", "discord", "slack", "skypeuripreview", "linkedin", "pinterest",
    "twitterbot", "applebot", "bingpreview", "headless", "phantomjs", "puppeteer", "playwright",
    "selenium", "python-requests", "python-urllib", "curl", "wget", "httpclient", "okhttp", "go-http",
    "axios", "node-fetch", "java/", "libwww", "scrapy", "monitor", "uptime", "lighthouse", "pagespeed",
  ].join("|"),
  "i",
);

/** True for crawlers, link-preview fetchers, headless browsers and scripts. */
export function isBotUserAgent(ua: string | null | undefined): boolean {
  if (!ua || ua.trim().length < 10) return true;
  return BOT_RE.test(ua);
}

export function parseUserAgent(ua: string | null | undefined): { device: string; browser: string; os: string } {
  const s = ua ?? "";
  const device = /ipad|tablet/i.test(s) ? "Tablet" : /mobi|iphone|android/i.test(s) ? "Mobile" : "Desktop";
  const browser = /edg\//i.test(s) ? "Edge"
    : /opr\/|opera/i.test(s) ? "Opera"
    : /samsungbrowser/i.test(s) ? "Samsung Internet"
    : /crios|chrome\//i.test(s) ? "Chrome"
    : /fxios|firefox\//i.test(s) ? "Firefox"
    : /safari\//i.test(s) ? "Safari"
    : "Other";
  const os = /iphone|ipad|ios/i.test(s) ? "iOS"
    : /android/i.test(s) ? "Android"
    : /windows/i.test(s) ? "Windows"
    : /mac os x|macintosh/i.test(s) ? "macOS"
    : /linux/i.test(s) ? "Linux"
    : "Other";
  return { device, browser, os };
}

/** Destination must be an internal path or an http(s) URL. */
export function isSafeDestination(dest: string | null | undefined): boolean {
  if (!dest) return false;
  if (dest.startsWith("/") && !dest.startsWith("//")) return true;
  try {
    const u = new URL(dest);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Appends UTM params without overwriting ones already on the destination. */
export function withUtm(dest: string, utm: Record<string, string> | null | undefined): string {
  const entries = Object.entries(utm ?? {}).filter(([k, v]) => /^utm_[a-z]+$/.test(k) && v);
  if (!entries.length) return dest;
  const internal = dest.startsWith("/");
  const u = new URL(dest, "https://podgo.io");
  for (const [k, v] of entries) if (!u.searchParams.has(k)) u.searchParams.set(k, v);
  return internal ? `${u.pathname}${u.search}${u.hash}` : u.toString();
}

/** Normalises a 404 path for grouping: lowercase, no trailing slash, no query. */
export function normalizeMissPath(path: string): string {
  let p = (path || "/").split("?")[0].split("#")[0].toLowerCase().slice(0, 300);
  if (!p.startsWith("/")) p = `/${p}`;
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

export async function sha256(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

const SEARCH_RE = /(^|\.)(google|bing|duckduckgo|yahoo|baidu|yandex|ecosia|brave|startpage)\./i;

/** Where a visit came from: our own pages, a search engine, another site, or nowhere (typed/QR/app). */
export function classifyReferrer(referrer: string | null | undefined, ownHost: string): "internal" | "external" | "search" | "direct" {
  if (!referrer) return "direct";
  let host: string;
  try { host = new URL(referrer).hostname.toLowerCase(); } catch { return "direct"; }
  const own = ownHost.replace(/^www\./, "");
  if (host === own || host.endsWith(`.${own}`) || host.endsWith("podgo.io")) return "internal";
  if (SEARCH_RE.test(host)) return "search";
  return "external";
}

// Social apps that fetch a page to build a share card. Real people never send these.
const PREVIEW_RE = /facebookexternalhit|facebot|twitterbot|linkedinbot|slackbot|discordbot|whatsapp|telegrambot|skypeuripreview|pinterest|redditbot|applebot|googlebot|bingbot|embedly|iframely|vkshare|mastodon|bluesky|imessage/i;

/** True for share-card fetchers that should get the link's preview image. */
export function isPreviewCrawler(ua: string | null | undefined): boolean {
  return !!ua && PREVIEW_RE.test(ua);
}

/** Artwork tracking codes are short lowercase letters/digits. */
export function isCreativeCode(code: unknown): code is string {
  return typeof code === "string" && /^[a-z0-9]{4,12}$/.test(code);
}

/**
 * Adds ?link=<slug> (and &lc=<artwork code>) to internal destinations so the
 * main site remembers which link and which graphic brought the visitor.
 */
export function addLinkParams(dest: string, slug: string, creative?: string | null, variant?: string | null): string {
  if (!dest.startsWith("/") || /[?&]link=/.test(dest)) return dest;
  const [path, hash] = dest.split("#");
  const extra = `link=${slug}${isCreativeCode(creative) ? `&lc=${creative}` : ""}${isCreativeCode(variant) ? `&lv=${variant}` : ""}`;
  return `${path}${path.includes("?") ? "&" : "?"}${extra}${hash ? `#${hash}` : ""}`;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

/** Minimal page for share-card fetchers: preview tags, then a hop to the real page. */
export function shareCardHtml(o: { title: string; description: string; image: string | null; url: string; target: string }): string {
  const img = o.image ? `<meta property="og:image" content="${esc(o.image)}"><meta name="twitter:image" content="${esc(o.image)}">` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(o.title)}</title>
<meta property="og:type" content="website"><meta property="og:title" content="${esc(o.title)}">
<meta property="og:description" content="${esc(o.description)}"><meta property="og:url" content="${esc(o.url)}">
<meta name="twitter:card" content="${o.image ? "summary_large_image" : "summary"}"><meta name="twitter:title" content="${esc(o.title)}">
${img}<meta http-equiv="refresh" content="0;url=${esc(o.target)}"></head><body><a href="${esc(o.target)}">Continue to PODGO</a></body></html>`;
}

export interface SplitVariant { id: string; code: string; destination: string; weight: number; is_active: boolean }

/**
 * Split-test pick. A returning visitor keeps the variant they already got
 * (when it is still live); everyone else is placed by weight.
 */
export function pickVariant<T extends SplitVariant>(variants: T[], sticky: string | null | undefined, rand = Math.random()): T | null {
  const live = variants.filter((v) => v.is_active && v.weight > 0);
  if (live.length === 0) return null;
  const kept = sticky ? live.find((v) => v.code === sticky) : undefined;
  if (kept) return kept;
  const total = live.reduce((n, v) => n + v.weight, 0);
  let r = rand * total;
  for (const v of live) { r -= v.weight; if (r < 0) return v; }
  return live[live.length - 1];
}
